/** Hook para gestión del modal de órdenes de trabajo de un equipo. */
import { useState, useCallback } from 'react';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';

/**
 * Gestiona el estado y lógica del modal de órdenes de trabajo de un equipo.
 *
 * @returns {Object} Estado y handlers del dominio de órdenes de trabajo
 * @returns {boolean} returns.ordenesTrabajoModalOpen - Estado de visibilidad del modal
 * @returns {Object|null} returns.selectedEquipoMant - Equipo cuyas OT se muestran
 * @returns {Array} returns.ordenesTrabajo - Últimas órdenes de trabajo del equipo
 * @returns {boolean} returns.otLoading - Estado de carga de órdenes
 * @returns {Function} returns.openOrdenesTrabajo - Abre el modal y carga OT del equipo
 * @returns {Function} returns.closeOrdenesTrabajo - Cierra el modal
 */
function useEquipoOrdenesTrabajo() {
  const { showToast } = useToast();

  const [ordenesTrabajoModalOpen, setOrdenesTrabajoModalOpen] = useState(false);
  const [selectedEquipoMant, setSelectedEquipoMant] = useState(null);
  const [ordenesTrabajo, setOrdenesTrabajo] = useState([]);
  const [otLoading, setOtLoading] = useState(false);

  /**
   * Abre el modal y carga las últimas órdenes de trabajo del equipo
   * @param {Object} equipo - Equipo a consultar
   * @returns {Promise<void>}
   */
  const openOrdenesTrabajo = useCallback(async (equipo) => {
    setSelectedEquipoMant(equipo);
    setOrdenesTrabajoModalOpen(true);
    setOtLoading(true);

    try {
      const response = await apiService.getOrdenesTrabajo({
        equipo_id: equipo.id,
        limit: 5,
        sortBy: 'fecha_ejecucion',
        sortOrder: 'desc'
      });

      if (response.success) {
        setOrdenesTrabajo(response.data || []);
      } else {
        setOrdenesTrabajo([]);
        showToast(response.message || 'Error al cargar órdenes de trabajo', 'error');
      }
    } catch (error) {
      showToast('Error al cargar órdenes de trabajo', 'error');
      setOrdenesTrabajo([]);
    } finally {
      setOtLoading(false);
    }
  }, [showToast]);

  /**
   * Cierra el modal de órdenes de trabajo
   */
  const closeOrdenesTrabajo = useCallback(() => {
    setOrdenesTrabajoModalOpen(false);
  }, []);

  return {
    ordenesTrabajoModalOpen,
    selectedEquipoMant,
    ordenesTrabajo,
    otLoading,
    openOrdenesTrabajo,
    closeOrdenesTrabajo,
  };
}

export default useEquipoOrdenesTrabajo;
